import { IVeriMapChildComponent, IVeriMapComponent, MapPopupType } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { BroadcastEvent } from "@enums/BroadcastEvent";
import { GeofenceType } from "@enums/GeofenceType";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { GeofenceCategory, IGeofence } from "@interfaces/IGeofences";
import { IPlot } from "@interfaces/IPlot";
import { IVehicle } from "@interfaces/IVehicle";
import { Debounce } from "@services/debounce.service";
import { GeofenceService, IShowGeofenceEditorOptions } from "@services/geofence.service";
import { SessionService } from "@services/session.service";
import * as angular from "angular";
import { IOnChangesObject } from "angular";
import { SettingsName } from "../../../class/SettingsName";
import { GeofenceOverlay } from "./geofenceoverlay";
import "./geofenceOverlay.scss";

export interface IGeofencesDirectiveScope extends angular.IScope {
    geofences: IGeofence[];
    selectedGeofence: IGeofence;
    highlightOnHover: boolean;
    showZones: boolean;
    hideGeofence: IGeofence;
    hidePopupControls: boolean;
    hideGeofencePopup: boolean;
    onlyRightClick: boolean;
    onlyShowSelectedGeofence: boolean;
    geofenceClicked: (args: { geofence: IGeofence, leftClick: boolean }) => void;
}

interface IGeofencePolygonGroup {
    geofence: IGeofence;
    polygons: google.maps.Polygon[];
}

export class GeofencesComponentController implements angular.IComponentController, IVeriMapChildComponent {
    // map
    veriMap: IVeriMapComponent;
    map: google.maps.Map;
    geofenceOverlays: GeofenceOverlay[] = [];
    mapEventListeners: google.maps.MapsEventListener[] = [];

    //
    geofences: IGeofence[];
    selectedGeofence: IGeofence;
    hideGeofence: IGeofence;
    showZones: boolean = false;
    onlyRightClick: boolean = false;
    previousBounds: google.maps.LatLngBounds;
    onlyShowSelectedGeofence: boolean;
    userOptions: ICombinedPreferences;

    hidePopupControls: boolean;
    hideGeofencePopup: boolean = false;

    //private lastMouseLatLng: google.maps.LatLng;
    private debouncedToggleVisibility: () => void;
    private highlightOnHover: boolean;
    private loadingGeofences = false;
    private geofenceClicked: (args: { geofence: IGeofence, leftClick: boolean }) => void;

    // zones
    private zonePolygonGroups: IGeofencePolygonGroup[] = [];

    static $inject = ["$scope", "$timeout", "geofenceService", "sessionService", "debounceService"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly $timeout: angular.ITimeoutService,
        private readonly geofenceService: GeofenceService,
        private readonly sessionService: SessionService,
        private readonly debounceService: Debounce,) {
    }

    $onInit() {
        this.$scope.$on(BroadcastEvent.openGeofenceEditor, (event, geofence: IGeofence, position: google.maps.LatLng = null, resource: IVehicle | IPlot = null) => {
            this.openGeofenceEditor(geofence, position, resource);
        });

        this.veriMap.registerChildComponent(this);

        this.veriMap.mapInitialisedPromise.then(map => {
            this.setMap(map);
        });

        this.debouncedToggleVisibility = this.debounceService.debounce(() => { this.toggleViewportGeofences(); }, 100, false);

        this.userOptions = this.sessionService.getItem<ICombinedPreferences>(SettingsName.UserOptions);
    }

    $onChanges(onChangesObj: IOnChangesObject): void {
        if (onChangesObj["hideGeofence"]) {
            this.toggleViewportGeofences();
        }

        if ((onChangesObj["geofences"] || onChangesObj["showZones"] || onChangesObj["selectedGeofence"]) && this.geofences) {
            if (!this.map) {
                this.veriMap.mapInitialisedPromise.then(map => {
                    this.setMap(map);
                    this.createGeofencesOverlays();
                    this.createGeofenceZones();
                });
            }
            else {
                this.createGeofencesOverlays();
                this.createGeofenceZones();
            }
        }
    }

    $onDestroy() {
        this.mapEventListeners.forEach(listener => {
            google.maps.event.removeListener(listener);
        });

        this.destroyOverlays();

        this.veriMap.deregisterChildComponent(this);
    }

    setMap(map: google.maps.Map) {
        if (this.map) {
            return;
        }

        this.map = map;

        const zoomListener = this.map.addListener("zoom_changed", () => {
            this.debouncedToggleVisibility();
        });

        const idleListener = this.map.addListener("idle", () => {
            this.debouncedToggleVisibility();
        });

        const mapTypeListener = this.map.addListener("maptypeid_changed", () => {
            this.geofenceOverlays.forEach(go => {
                go.updateColoursForMapType();
                go.drawGeofence(true);
            });
        });

        const clickListener = this.map.addListener("click", (mouseEvent: google.maps.MouseEvent) => {
            this.mapClicked(mouseEvent.latLng);

            // used it Geofences UI to cancel out the add geofence popup on map click
            if (this.onlyRightClick) {
                this.veriMap.hidePopup(MapPopupType.RightClick, true);
            }
        });

        const rightClickListener = this.map.addListener("rightclick", (mouseEvent: google.maps.MouseEvent) => {
            this.mapClicked(mouseEvent.latLng, false);
        });

        //const mouseMoveListener = this.map.addListener("mousemove", (mouseEvent: google.maps.MouseEvent) => {
        //    this.lastMouseLatLng = mouseEvent.latLng;
        //    this.debouncedMapMouseMove();
        //});

        this.mapEventListeners.push(mapTypeListener);
        this.mapEventListeners.push(idleListener);
        this.mapEventListeners.push(clickListener);
        this.mapEventListeners.push(rightClickListener);
        this.mapEventListeners.push(zoomListener);
        //this.mapEventListeners.push(mouseMoveListener);
    }

    onMouseMove(mousePosition: google.maps.LatLng, event: Event): boolean {

        var closestOverlayMatch: GeofenceOverlay = null;
        var closestOverlayMatchDistance = 0;

        for (var i = 0; i < this.geofenceOverlays.length; i++) {
            const overlay = this.geofenceOverlays[i];

            if (!overlay || !overlay.overlay || !overlay.visible) {
                continue;
            }

            // Reset hover status
            overlay.setHover(false);

            // Note this works on a square so isnt 100% accurate for polygons and edges of circles
            if (overlay.bounds.contains(mousePosition) && this.geofenceService.pointIsInsideGeofence(mousePosition, overlay.geofence)) {

                if (!this.userOptions.GeofenceLabelWhenZoomedOut.Value) {
                    overlay.setGeofenceClass(true);
                }

                var distanceFromPointToGeofence = google.maps.geometry.spherical.computeDistanceBetween(mousePosition, overlay.geofence.MapCoords);

                if (closestOverlayMatch == null || distanceFromPointToGeofence < closestOverlayMatchDistance) {

                    closestOverlayMatch = overlay;
                }
            }
        }

        if (closestOverlayMatch) {
            this.highlightGeofenceOverlay(closestOverlayMatch);
        }
        else if (!this.selectedGeofence) {
            this.veriMap.hidePopup(MapPopupType.Geofence, false);
        }

        return false;
    }

    // -------------------------------------------------

    private highlightGeofenceOverlay(overlay: GeofenceOverlay, event?: google.maps.MouseEvent): void {

        if (!overlay.visible) {
            return;
        }

        // hides geofence popup in geofences tab
        if (this.onlyRightClick || this.hideGeofencePopup) {
            this.veriMap.hidePopup(MapPopupType.Geofence, true);

            return;

        }
        else {
            overlay.setHover(true);

            const ne = overlay.bounds.getNorthEast();
            const center = overlay.bounds.getCenter();
            const latLng = new google.maps.LatLng(ne.lat(), center.lng());

            this.veriMap.showPopup(MapPopupType.Geofence, latLng, overlay.geofence, this.hidePopupControls);
        }

        this.$scope.$apply();
    }

    private loadGeofences(invalidateCache = false) {
        this.loadingGeofences = true;

        return this.geofenceService
            .getGeofences({ includeTags: true, invalidateCache })
            .then((geofences) => {
                this.loadingGeofences = false;
                this.geofences = geofences;
                this.geofences = this.addNonexistentUnsavedSelectedGeofence(this.geofences, this.selectedGeofence);

                this.createGeofencesOverlays();
                this.createGeofenceZones();

                return this.geofences;
            })
            .finally(() => {
                this.loadingGeofences = false;
            });
    }

    private addNonexistentUnsavedSelectedGeofence(geofences: IGeofence[] = [], selectedGeofence: IGeofence): IGeofence[] {
        // this method allows us to display a preview of a geofence that is being created but not saved (doesn't exist)
        if (!selectedGeofence || selectedGeofence?.Id !== -1) {
            return geofences;
        }

        return [...geofences, { ...selectedGeofence, Description: `New Geofence` }];
    }

    private createGeofencesOverlays() {
        if (!this.userOptions.Geofences?.Value) {
            return;
        }

        const standardGeofences = this.geofences.filter(category => category?.Category === GeofenceCategory.Standard);

        this.destroyOverlays();

        this.geofenceOverlays = this.geofenceService.createGeofenceOverlays(this.map, standardGeofences, this.userOptions.GeofenceLabelWhenZoomedOut.Value/*, true*/);
        this.toggleGeofences(this.map.getBounds());
    }

    private createGeofenceZones(): void {
        this.removeDisplayZones();

        const zoneGeofences = this.geofences.filter(geofence => geofence.Category === GeofenceCategory.Zone);

        let shownZones: IGeofence[] = [];

        if (this.userOptions.ShowZones?.Value) {
            shownZones.push(...zoneGeofences.filter(geofence => geofence.AccountId != null));
        }
        
        if (this.userOptions.ViewSystemGeofences?.Value) {
            shownZones.push(...zoneGeofences.filter(geofence => geofence.AccountId == null));
        }

        shownZones.forEach(zone => {
            if (zone?.GeofenceType !== GeofenceType.Polygon) {
                return;
            }

            const polygons = this.geofenceService.createPolygonGroupForGeofence(zone);
            this.zonePolygonGroups.push({
                geofence: zone,
                polygons: polygons
            });

            polygons.forEach(polygon => {
                polygon.setMap(this.map);
            });
        });

        this.processZoneVisibility(this.map?.getBounds());
    }

    private removeDisplayZones(): void {
        this.zonePolygonGroups?.forEach(zonePolygonGroup => zonePolygonGroup?.polygons?.forEach(zonePolygon => zonePolygon.setMap(null)));
        this.zonePolygonGroups = [];
    }

    private processZoneVisibility(mapBounds: google.maps.LatLngBounds): void {
        const currentMapZoomLevel = this.map?.getZoom();
        const minimumVisibilityZoomLevel = 6;

        const maxZoomLevel = 10;
        const minZoomLevel = 5;

        const maxZoomOpacity = 0;
        const minZoomOpacity = 0.2;

        const maxZoomLevels = maxZoomLevel - minZoomLevel;
        const maxZoomOpacityChange = maxZoomOpacity - minZoomOpacity;

        const opacityChangePerZoomLevel = maxZoomOpacityChange / maxZoomLevels;

        let opacity = minZoomOpacity;

        if (currentMapZoomLevel > minZoomLevel) {
            opacity += opacityChangePerZoomLevel * (currentMapZoomLevel - minZoomLevel);
            opacity = Math.min(Math.max(opacity, 0), 100);
        }

        this.zonePolygonGroups?.forEach(zonePolygonGroup => {
            const isSelectedGeofence = this.selectedGeofence?.Id === zonePolygonGroup?.geofence?.Id;

            zonePolygonGroup?.polygons?.forEach(zonePolygon => {
                if (currentMapZoomLevel >= minimumVisibilityZoomLevel) {
                    zonePolygon?.setOptions({
                        strokeOpacity: 0.3,
                        strokeColor: "rgb(0, 0, 0)",
                        strokeWeight: 1,
                        fillColor: "rgb(0, 0, 0)",
                        fillOpacity: isSelectedGeofence ? 0.1 : opacity,
                        clickable: false,
                        draggable: false
                    });
                }
            });
        });
    }

    private toggleViewportGeofences() {

        if (!this.map) {
            return;
        }

        const bounds = this.map.getBounds();

        if (this.previousBounds != null && this.previousBounds === bounds) {
            // If our viewport hasnt changed don't re-render
            return;
        }

        this.toggleGeofences(bounds);
        this.processZoneVisibility(bounds);
    }

    private toggleGeofences(bounds: google.maps.LatLngBounds) {
        if (this.loadingGeofences) {
            return;
        }

        this.previousBounds = bounds;
        const zoomLevel = this.map.getZoom();
        const visibleForZoomLevel = zoomLevel > 10;

        // This allows for lazy loading
        if (visibleForZoomLevel && !this.geofences) {
            this.loadGeofences();

            return;
        }

        for (let i = 0; i < this.geofenceOverlays.length; i++) {
            const overlay = this.geofenceOverlays[i];

            if (!overlay || !overlay.geofence) {
                continue;
            }

            if (this.hideGeofence && this.hideGeofence.Id === overlay.geofence.Id) {
                overlay.hideElement();
                overlay.setHover(false);
                continue;
            }

            if (visibleForZoomLevel && !overlay.geofence.HideFromMap && bounds.intersects(overlay.bounds)) {

                overlay.showElement();
                overlay.setHover(false);

                if (this.selectedGeofence && this.selectedGeofence.Id === overlay.geofence.Id) {
                    this.$timeout(() => {
                        this.highlightGeofenceOverlay(overlay);
                    }, 250);
                }
            }
            else {
                overlay.hideElement();
            }

            if (this.onlyShowSelectedGeofence && this.selectedGeofence && overlay.geofence.Id != this.selectedGeofence.Id) {
                overlay.hideElement();
                overlay.setHover(false);
            }
        }
    }

    private mapClicked(latLng: google.maps.LatLng, leftClick = true) {
        if (!this.geofenceClicked) {
            return;
        }

        for (let i = 0; i < this.geofenceOverlays.length; i++) {
            const overlay = this.geofenceOverlays[i];

            if (overlay && overlay.bounds.contains(latLng)) {
                this.geofenceClicked({ geofence: overlay.geofence, leftClick: leftClick });
                break;
            }
        }
    }

    private destroyOverlays() {
        this.geofenceOverlays.forEach((overlay) => {
            overlay.hideElement();
            overlay.setHover(false);

            if (overlay && overlay.overlay) {
                overlay.overlay.setMap(null);
            }
        });
    }

    private openGeofenceEditor(geofence?: IGeofence, position: google.maps.LatLng = null, resource: IVehicle | IPlot = null) {
        position = (position) ? position : new google.maps.LatLng(geofence?.Latitude, geofence?.Longitude);

        let options: IShowGeofenceEditorOptions = {
            focusedLatLngs: position,
            resource: resource,
            onGeofenceEditorCancelled: () => { this.onGeofenceEditorCancelled(); },
            onGeofenceEditorSaved: (savedGeofence: IGeofence) => { this.onGeofenceEditorSaved(savedGeofence); },
            onGeofenceEditorDeleted: (deletedGeofence: IGeofence) => { this.onGeofenceEditorDeleted(deletedGeofence); }
        };

        this.geofenceService.showGeofenceEditor(geofence, null, options);
    }

    private onGeofenceEditorCancelled(): void { }

    private onGeofenceEditorSaved(savedGeofence: IGeofence): void {
        this.loadGeofences(true);
    }

    private onGeofenceEditorDeleted(deletedGeofence: IGeofence): void {
        this.loadGeofences(true);
    }
}

export class GeofencesComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    require: { [index: string]: string; };

    constructor() {
        this.bindings = {
            hidePopupControls: "<",
            hideGeofence: "<",
            showZones: "<?",
            geofences: "<?",
            selectedGeofence: "<?",
            onlyRightClick: "<?",
            hideGeofencePopup: "<?",
            onlyShowSelectedGeofence: "<?",
            geofenceClicked: "&"
        };

        this.require = {
            veriMap: "^veriMap"
        };

        this.controller = GeofencesComponentController;
    }
}
